<template>
  <!-- ============================================================ -->
  <!-- 收款页 — 全球收款·极速安全·一站式支付解决方案                  -->
  <!-- ============================================================ -->
  <div class="payment">
    <!-- ==================== 1. Hero 横幅 ==================== -->
    <section class="payment-hero">
      <div class="payment-hero__inner">
        <!-- 左侧文案区 -->
        <div class="payment-hero__content">
          <p class="payment-hero__tag">
            {{ $t('全球20万+商户信赖') }}
          </p>
          <h1 class="payment-hero__title">
            <div class="payment-hero__title-row">
              <span class="payment-hero__title-line">{{ $t('全球收款') }}</span>
              <span class="payment-hero__title-line payment-hero__title-line--accent">{{ $t('极速安全') }}</span>
            </div>
            <span class="payment-hero__title-line">{{ $t(' 一站式支付解决方案') }}</span>
          </h1>
          <p class="payment-hero__desc">
            {{ $t('集成 PayPal、Stripe、支付宝、微信支付等 100+ 支付渠道，多币种智能结算，资金直达您的账户 —— 无隐形费用，5分钟开通全球生意。') }}
          </p>
          <div class="payment-hero__actions">
            <button
              class="payment-hero__btn payment-hero__btn--primary"
              @click="$router.push('/sign-up')"
            >
              {{ $t('立即开始收款') }}
            </button>
            <button
              class="payment-hero__btn payment-hero__btn--outline"
              @click="scrollToSection('payment-faq')"
            >
              {{ $t('联系专家') }}
            </button>
          </div>
          <ul class="payment-hero__trust">
            <li class="payment-hero__trust-item">
              <span class="payment-hero__trust-icon">✅</span>
              <span>{{ $t('无平台手续费') }}</span>
            </li>
            <li class="payment-hero__trust-item">
              <span class="payment-hero__trust-icon">✅</span>
              <span>{{ $t('多币种实时汇率') }}</span>
            </li>
            <li class="payment-hero__trust-item">
              <span class="payment-hero__trust-icon">✅</span>
              <span>{{ $t('PCI 认证加密') }}</span>
            </li>
          </ul>
        </div>
        <!-- 右侧：模拟结算卡片 -->
        <div class="payment-hero__calculator">
          <div class="payment-calc">
            <div class="payment-calc__header">
              <span class="payment-calc__label">{{ $t('模拟结算') }}</span>
              <span class="payment-calc__badge">{{ $t('实时汇率') }}</span>
            </div>
            <label class="payment-calc__field-label">{{ $t('金额 (USD)') }}</label>
            <div class="payment-calc__input-wrap">
              <input
                v-model.number="usdAmount"
                type="number"
                class="payment-calc__input"
                min="0"
                step="0.01"
                placeholder="299.00"
              >
              <span class="payment-calc__input-icon">💱</span>
            </div>
            <div
              v-for="currency in convertedCurrencies"
              :key="currency.code"
              class="payment-calc__row"
            >
              <span class="payment-calc__row-label">{{ currency.flag }} {{ currency.code }}</span>
              <span class="payment-calc__row-value">{{ currency.symbol }}{{ currency.amount }}</span>
            </div>
            <p class="payment-calc__disclaimer">
              {{ $t('* 展示参考汇率，实际以支付机构为准') }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== 2. earthwork 标题区 ==================== -->
    <section class="payment-network">
      <div class="payment-network__inner">
        <p class="payment-network__label">
          {{ $t('全球支付网络') }}
        </p>
        <h2 class="payment-network__title">
          {{ $t('支持 100+ 支付方式 · 覆盖 200+ 国家') }}
        </h2>
        <p class="payment-network__desc">
          {{ $t('从国际信用卡到本地钱包，满足全球买家本地化支付习惯，提升转化率') }}
        </p>
      </div>
    </section>

    <!-- ==================== 3. Tab 标签栏 + 内容区 ==================== -->
    <section class="payment-tabs">
      <div class="payment-tabs__inner">
        <!-- Tab 头部 -->
        <div class="payment-tabs__header">
          <button
            v-for="(tab, idx) in paymentTabs"
            :key="tab.key"
            :class="['payment-tabs__btn', { 'payment-tabs__btn--active': activeTab === idx }]"
            @click="activeTab = idx"
          >
            {{ $t(tab.label) }}
          </button>
        </div>
        <div class="payment-tabs__indicator">
          <div
            class="payment-tabs__indicator-bar"
            :style="{ transform: `translateX(${activeTab * 100}%)` }"
          />
        </div>
        <!-- Tab 内容 -->
        <div class="payment-tabs__content">
          <!-- Tab 0: PayPal 生态 -->
          <div
            v-show="activeTab === 0"
            class="payment-tabs__panel"
          >
            <div class="payment-tab-content">
              <div class="payment-tab-content__text">
                <h3 class="payment-tab-content__title">
                  {{ $t(paymentTabs[0].content.title) }}
                </h3>
                <p class="payment-tab-content__desc">
                  {{ $t(paymentTabs[0].content.desc) }}
                </p>
                <div class="payment-tab-content__badge">
                  <img
                    :src="paymentTabs[0].content.badgeImg"
                    alt=""
                    loading="lazy"
                  >
                </div>
              </div>
              <img
                class="payment-tab-content__img"
                :src="paymentTabs[0].content.rightImg"
                alt=""
                loading="lazy"
              >
            </div>
          </div>
          <!-- Tab 1: 国际信用卡 -->
          <div
            v-show="activeTab === 1"
            class="payment-tabs__panel"
          >
            <div class="payment-tab-content">
              <div class="payment-tab-content__text">
                <h3 class="payment-tab-content__title">
                  {{ $t(paymentTabs[1].content.title) }}
                </h3>
                <p class="payment-tab-content__desc">
                  {{ $t(paymentTabs[1].content.desc) }}
                </p>
                <div class="payment-tab-content__logos">
                  <img
                    :src="require('@/assets/images/payment/earthwork-2-idcards.png')"
                    alt=""
                    class="payment-tab-content__logo"
                    loading="lazy"
                  >
                </div>
              </div>
              <img
                class="payment-tab-content__img"
                :src="paymentTabs[1].content.rightImg"
                alt=""
                loading="lazy"
              >
            </div>
          </div>
          <!-- Tab 2: 本地钱包 -->
          <div
            v-show="activeTab === 2"
            class="payment-tabs__panel"
          >
            <div class="payment-tab-content">
              <div class="payment-tab-content__text">
                <h3 class="payment-tab-content__title">
                  {{ $t(paymentTabs[2].content.title) }}
                </h3>
                <p class="payment-tab-content__desc">
                  {{ $t(paymentTabs[2].content.desc) }}
                </p>
                <div class="payment-tab-content__logos">
                  <img
                    :src="require('@/assets/images/payment/earthwork-3-pay.png')"
                    alt=""
                    class="payment-tab-content__logo"
                    loading="lazy"
                  >
                </div>
              </div>
              <img
                class="payment-tab-content__img"
                :src="paymentTabs[2].content.rightImg"
                alt=""
                loading="lazy"
              >
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== 4. choose us 4. 为什么选择我们？ ==================== -->
    <section class="payment-why">
      <div class="payment-why__inner">
        <h2 class="payment-why__title">
          {{ $t('为什么选择 Taoify 收款？') }}
        </h2>
        <p class="payment-why__subtitle">
          {{ $t('智能 · 安全 · 极简接入') }}
        </p>
        <div class="payment-why__grid">
          <div
            v-for="feature in whyFeatures"
            :key="feature.title"
            class="payment-why__card"
          >
            <img
              :src="feature.icon"
              alt=""
              class="payment-why__card-icon"
              loading="lazy"
            >
            <h3 class="payment-why__card-title">
              {{ $t(feature.title) }}
            </h3>
            <p class="payment-why__card-desc">
              {{ $t(feature.descKey) }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== 5. 多币种功能 + 汇率计算 ==================== -->
    <section class="payment-multi">
      <div class="payment-multi__inner">
        <div class="payment-multi__features">
          <div class="payment-multi__features-head">
            <span class="payment-multi__features-icon">💱</span>
            <h2 class="payment-multi__features-title">
              {{ $t('智能多货币 · 全球本地化定价') }}
            </h2>
          </div>
          <p class="payment-multi__features-desc">
            {{ $t('系统自动识别买家地区，展示当地货币价格；后台可设置不同国家阶梯定价，汇率定时更新，灵活运营全球市场。') }}
          </p>
          <div class="payment-multi__carousel">
            <div
              class="payment-multi__carousel-track"
              :style="{ transform: `translateY(-${currencyFeatureIdx * 32}px)` }"
            >
              <div
                v-for="(item, i) in currencyFeatures"
                :key="i"
                :class="['payment-multi__carousel-item', { 'payment-multi__carousel-item--active': currencyFeatureIdx === i }]"
                @click="currencyFeatureIdx = i"
              >
                <span class="payment-multi__carousel-icon">{{ item.icon }}</span>
                <div class="payment-multi__carousel-text">
                  <p>{{ $t(item.line1) }}</p>
                  <p>{{ $t(item.line2) }}</p>
                  <p>{{ $t(item.line3) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- 汇率计算示例 -->
        <div class="payment-multi__rates">
          <h3 class="payment-multi__rates-title">
            {{ $t('汇率计算示例') }}
          </h3>
          <div
            v-for="rate in exchangeRates"
            :key="rate.label"
            class="payment-multi__rates-row"
          >
            <span class="payment-multi__rates-from">{{ $t('1 USD →') }}</span>
            <span class="payment-multi__rates-to">{{ $t(rate.to) }}</span>
          </div>
          <div class="payment-multi__rates-tip">
            <span>💡</span>
            <span>{{ $t('商家端可选择结算币种: 美元/欧元/英镑/人民币，规避汇损风险') }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== 6. bank level security 银行级安全 ==================== -->
    <section class="payment-security">
      <div class="payment-security__inner">
        <h2 class="payment-security__title">
          {{ $t('银行级安全 · 全方位资金保障') }}
        </h2>
        <div class="payment-security__grid">
          <div
            v-for="item in securityFeatures"
            :key="item.title"
            class="payment-security__card"
          >
            <div class="payment-security__card-icon">
              <img
                v-if="item.icon"
                :src="item.icon"
                alt=""
                loading="lazy"
              >
              <div
                v-else
                class="payment-security__card-icon-fallback"
              >
                <span>{{ item.fallback }}</span>
              </div>
            </div>
            <div class="payment-security__card-text">
              <h3 class="payment-security__card-title">
                {{ $t(item.title) }}
              </h3>
              <p class="payment-security__card-desc">
                {{ $t(item.descKey) }}
              </p>
            </div>
          </div>
        </div>
        <div class="payment-security__commit">
          <div class="payment-security__commit-left">
            <h3 class="payment-security__commit-title">
              {{ $t('Taoify 承诺') }}
            </h3>
            <p class="payment-security__commit-text">
              {{ $t('❌ 不收取任何交易手续费 ❌ 不挪用资金 ❌ 不冻结合规资金') }}
            </p>
          </div>
          <span class="payment-security__commit-highlight">{{ $t('资金直达您的支付账户') }}</span>
        </div>
      </div>
    </section>

    <!-- ==================== 7. 3步极速接入 ==================== -->
    <section
      ref="stepsSection"
      class="payment-steps"
    >
      <div class="payment-steps__inner">
        <h2 class="payment-steps__title">
          {{ $t('3步极速接入 · 5分钟即可收款') }}
        </h2>
        <p class="payment-steps__subtitle">
          {{ $t('无需技术团队，跟着指引轻松开通全球收款能力') }}
        </p>
        <div class="payment-steps__divider" />
        <div class="payment-steps__timeline">
          <div
            v-for="(step, idx) in integrationSteps"
            :key="idx"
            class="payment-steps__item"
          >
            <div class="payment-steps__badge">
              {{ idx + 1 }}
            </div>
            <div class="payment-steps__content">
              <h3 class="payment-steps__item-title">
                {{ $t(step.title) }}
              </h3>
              <p class="payment-steps__item-desc">
                {{ $t(step.descKey) }}
              </p>
            </div>
          </div>
        </div>
        <div class="payment-steps__action">
          <button
            class="payment-steps__link"
            @click="scrollToSection('payment-faq')"
          >
            <span>📖</span>
            <span>{{ $t('查看详细配置文档 →') }}</span>
          </button>
        </div>
      </div>
    </section>

    <!-- ==================== 8. 常见问题 ==================== -->
    <section
      ref="faqSection"
      class="payment-faq"
    >
      <h2 class="payment-faq__title">
        {{ $t('常见问题') }}
      </h2>
      <dl class="payment-faq__list">
        <div
          v-for="(faq, idx) in faqList"
          :key="idx"
          :class="['payment-faq__item', { 'payment-faq__item--open': faq.open }]"
        >
          <dt
            class="payment-faq__q"
            @click="toggleFaq(idx)"
          >
            {{ $t(faq.q) }}
          </dt>
          <dd
            v-if="faq.a"
            v-show="faq.open"
            class="payment-faq__a"
          >
            {{ $t(faq.a) }}
          </dd>
        </div>
      </dl>
    </section>
  </div>
</template>

<script>
  export default {
    name: 'Payment',
    data () {
      return {
        /* ---------- Hero / 计算器 ---------- */
        usdAmount: 299,
        /** 各币种兑 USD 汇率 */
        fxRates: {
          CNY: 7.2,
          EUR: 0.92,
          GBP: 0.79,
          JPY: 145.2,
        },
        currencyFlags: {
          CNY: '🇨🇳',
          EUR: '🇪🇺',
          GBP: '🇬🇧',
          JPY: '🇯🇵',
        },
        currencySymbols: {
          CNY: '¥',
          EUR: '€',
          GBP: '£',
          JPY: '¥',
        },

        /* ---------- Tab ---------- */
        activeTab: 0,
        paymentTabs: [
          {
            key: 'paypal',
            label: 'PayPal 生态',
            content: {
              title: '全球通用 PayPal 支付方案',
              desc: '支持标准 PayPal、Express 快捷支付及 Pay Later 分期服务，覆盖全球 200 +国家和地区，兼顾便捷付款与消费分期，有效提升独立站转化率与用户支付体验。',
              badgeImg: require('@/assets/images/payment/earthwork-1-paypal.png'),
              rightImg: require('@/assets/images/payment/earthwork-1.png'),
              logos: [],
            },
          },
          {
            key: 'card',
            label: '国际信用卡',
            content: {
              title: '主流信用卡全覆盖',
              desc: '接入 Visa、MasterCard、American Express、JCB、Discover 及银联等全球主流卡组织，支持多币种结算，适配欧美、亚太等市场用户的信用卡支付习惯。',
              rightImg: require('@/assets/images/payment/earthwork-2.png'),
            },
          },
          {
            key: 'wallet',
            label: '本地钱包',
            content: {
              title: '全球本地支付渠道集成',
              desc: '整合支付宝、微信、云闪付等中国本土支付，以及非洲 Paystack、欧洲 Klarna、荷兰 iDeal、德国 Sofort/Giropay 等区域主流支付方式，覆盖不同市场用户的支付偏好，降低支付拒付率',
              rightImg: require('@/assets/images/payment/earthwork-3.png'),
            },
          },
        ],

        /* ---------- 为什么选择 Taoify ---------- */
        whyFeatures: [
          {
            title: '多货币结算',
            descKey: '100+货币自动换算，买家端按本币展示，提高订单转化',
            icon: require('@/assets/images/payment/chooseus-1.png'),
          },
          {
            title: '资金安全',
            descKey: '资金由持牌支付机构托管，平台绝不触碰，交易数据加密',
            icon: require('@/assets/images/payment/chooseus-2.png'),
          },
          {
            title: '5分钟配置',
            descKey: '后台复制API密钥即可启用，无需开发，支持沙箱测试',
            icon: require('@/assets/images/payment/chooseus-3.png'),
          },
          {
            title: '高转化率',
            descKey: '集成Apple Pay/Google Pay，一键结账，降低购物车弃单',
            icon: require('@/assets/images/payment/chooseus-4.png'),
          },
        ],

        /* ---------- 多币种特性轮播 ---------- */
        currencyFeatureIdx: 0,
        currencyFeatures: [
          {
            icon: '📊',
            line1: '支持 USD, EUR, GBP, JPY, CAD, AUD, CNY 等主流货币',
            line2: '每日自动同步银行汇率，也可自定义溢价系数',
            line3: '多币种报表统一结算，对账清晰高效',
          },
          {
            icon: '🌐',
            line1: '智能识别买家所在国家/地区',
            line2: '自动匹配最优支付币种展示',
            line3: '降低汇率顾虑，提升下单信心',
          },
          {
            icon: '⚙️',
            line1: '后台灵活配置各国定价策略',
            line2: '支持阶梯汇率与溢价自定义',
            line3: '数仓级报表，精细化管理收益',
          },
        ],

        /* ---------- 汇率示例 ---------- */
        exchangeRates: [
          { label: 'CNY', to: '7.20 CNY' },
          { label: 'EUR', to: '0.92 EUR' },
          { label: 'GBP', to: '0.79 GBP' },
          { label: 'JPY', to: '145.20 JPY' },
        ],

        /* ---------- 3步接入 ---------- */
        integrationSteps: [
          { title: '选择支付渠道', descKey: 'PayPal / 信用卡 / 本地支付 自由组合' },
          { title: '填写 API 凭证', descKey: '后台一键绑定商户号，自动获取密钥' },
          { title: '测试并上线', descKey: '1元测试 → 真实订单 → 正式收款' },
        ],

        /* ---------- 常见问题 ---------- */
        faqList: [
          {
            q: 'Taoify 会收取交易手续费吗？',
            a: 'Taoify 本身不抽取任何交易手续费！仅支付渠道方（如PayPal、信用卡组织）按各自标准收取，平台零加价。',
            open: false,
          },
          {
            q: '支持哪些国家的本地支付？',
            a: '目前覆盖中国(支付宝/微信)、欧洲(Klarna,Sofort,iDEAL)、非洲(Paystack)、北美等地区，更多本地支付持续接入中。',
            open: false,
          },
          {
            q: '资金多久到账？安全吗？',
            a: 'PayPal 即时到账，信用卡结算通常1-3个工作日。资金由持牌机构托管，平台无任何挪用风险，交易全程加密',
            open: false,
          },
          {
            q: '需要自己申请支付网关账户吗？',
            a: '是的，商家需要自行申请 PayPal / Stripe 等商户账户，Taoify 提供详细配置指引及一对一客服协助绑定。',
            open: false,
          },
          {
            q: '是否支持人民币结算？',
            a: '支持！商家可以设置人民币为最终结算货币，全球买家支付时自动换算，规避外汇损失',
            open: true,
          },
        ],

        /* ---------- 安全特性 ---------- */
        securityFeatures: [
          { title: '第三方托管', descKey: '平台0触碰', icon: require('@/assets/images/payment/banklevel-1.png'), fallback: '' },
          { title: 'PCI DSS 认证', descKey: '支付数据加密', icon: require('@/assets/images/payment/banklevel-2.png'), fallback: '' },
          { title: '智能风控', descKey: '实时拦截欺诈', icon: require('@/assets/images/payment/banklevel-3.png'), fallback: '' },
          { title: '争议仲裁', descKey: '卖家保护计划', icon: require('@/assets/images/payment/banklevel-4.png'), fallback: '' },
          { title: '完整账单', descKey: '每笔可追溯', icon: require('@/assets/images/payment/banklevel-5.png'), fallback: '' },
          { title: '登录保护', descKey: 'MFA/操作日志', icon: require('@/assets/images/payment/banklevel-6.png'), fallback: '' },
        ],
      }
    },
    computed: {
      currentYear () {
        return new Date().getFullYear()
      },
      /** 各币种转换后的展示列表 */
      convertedCurrencies () {
        const codes = ['CNY', 'EUR', 'GBP', 'JPY']
        return codes.map((code) => {
          const raw = (this.usdAmount || 0) * this.fxRates[code]
          const decimals = code === 'JPY' ? 0 : 2
          return {
            code,
            flag: this.currencyFlags[code],
            symbol: this.currencySymbols[code],
            amount: raw.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }),
          }
        })
      },
    },
    mounted () {
      // 自动轮播多币种特性
      this._currencyTimer = setInterval(() => {
        this.currencyFeatureIdx = (this.currencyFeatureIdx + 1) % this.currencyFeatures.length
      }, 4000)
      // Tab 自动轮播
      this._tabTimer = setInterval(() => {
        this.activeTab = (this.activeTab + 1) % this.paymentTabs.length
      }, 5000)
    },
    beforeDestroy () {
      clearInterval(this._currencyTimer)
      clearInterval(this._tabTimer)
    },
    methods: {
      toggleFaq (idx) {
        const item = this.faqList[idx]
        // 手风琴模式：先关闭全部，再打开当前
        const wasOpen = item.open
        this.faqList.forEach(f => { f.open = false })
        item.open = !wasOpen
      },
      scrollToSection (ref) {
        const el = this.$refs[ref]
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      },
    },
  }
</script>

<style scoped lang="scss">
/* ================================================================
   CSS 变量 — 设计令牌 (1:1 对齐蓝湖标注)
   ================================================================ */
.payment {
  --c-primary: #7331ff;
  --c-primary-dark: #5f2eea;
  --c-primary-gradient: linear-gradient(360deg, #aa98fc 0%, #7331ff 100%);
  --c-primary-gradient-45: linear-gradient(45deg, #5f2eea 0%, #a259ff 100%);
  --c-text-heading: #333333;
  --c-text-body: #666666;
  --c-text-muted: #999999;
  --c-text-dark: #1f2937;
  --c-text-gray: #4b5563;
  --c-text-light-gray: #6b7280;
  --c-text-extra: #374151;
  --c-white: #ffffff;
  --c-bg-light: #f9fafb;
  --c-bg-purple-light: #faf5ff;
  --c-bg-footer: #e6e6e6;
  --c-border: #e5e7eb;
  --c-border-light: #ededed;
  --c-border-card: #f3f4f6;
  --c-border-purple: #f3e8ff;
  --c-green: #16a34a;
  --c-gray-light: #9ca3af;
  --max-w: 1200px;
  --r-xs: 4px;
  --r-md: 12px;
  --r-lg: 16px;
  --r-xl: 24px;
  --r-xxl: 34px;
  --r-tag: 47px;
  --r-pill: 9999px;
  --r-round: 50%;

  background-color: var(--c-white);
  font-family: 'Source Han Sans CN', 'PingFang SC', 'Microsoft YaHei', 'Roboto', 'Inter', sans-serif;
  color: var(--c-text-heading);
  overflow-x: hidden;
}

/* ================================================================
   1. Hero 横幅
   ================================================================ */
.payment-hero {
  position: relative;
  min-height: 720px;
  background: url('~@/assets/images/payment/Hero-1.png') center no-repeat;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;

  &__inner {
    max-width: var(--max-w);
    width: 100%;
    margin: 0 auto;
    padding: 110px 0 103px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  &__content {
    flex: 0 0 500px;
    padding-top: 44px;
  }

  /* 标签 pill */
  &__tag {
    display: inline-block;
    background: rgba(255, 255, 255, 0.6);
    border-radius: var(--r-tag);
    padding: 4px 12px;
    font-size: 16px;
    letter-spacing: 1px;
    color: #000;
    margin: 0;
    line-height: 24px;
  }

  /* 主标题 */
  &__title {
    margin: 20px 0 0;
    font-size: 52px;
    font-weight: 700;
    font-family: 'Source Han Sans CN', sans-serif;
    line-height: 1.2;
    color: var(--c-white);
  }
  &__title-row {
    display: block;
    font-size: inherit;
  }
  &__title-line {
    display: inline;
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    &--accent { color: #ceb7ff; padding-left: 10px; }
  }

  &__desc {
    margin: 12px 0 0;
    font-size: 14px;
    letter-spacing: 1px;
    font-weight: 500;
    color: var(--c-white);
    line-height: 1.5;
    max-width: 500px;
  }

  /* CTA 按钮区 */
  &__actions {
    display: flex;
    gap: 20px;
    margin-top: 44px;
  }
  &__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    cursor: pointer;
    font-size: 18px;
    font-weight: 500;
    font-family: 'Source Han Sans CN', sans-serif;
    border-radius: var(--r-xxl);
    height: 51px;
    transition: opacity 0.15s;
    &:hover { opacity: 0.9; }
    &:active { opacity: 0.7; }

    &--primary {
      width: 180px;
      background: var(--c-primary-gradient);
      color: var(--c-white);
      box-shadow: 0 4px 4px 0 rgba(103, 91, 230, 0.2);
    }
    &--outline {
      width: 180px;
      background: var(--c-white);
      color: #0f2128;
      box-shadow: 0 4px 18px 0 rgba(103, 91, 230, 0.1);
    }
  }

  /* 信任徽章 */
  &__trust {
    list-style: none;
    display: flex;
    gap: 20px;
    margin: 16px 0 0;
    padding: 0;
  }
  &__trust-item {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 14px;
    font-family: Inter, sans-serif;
    color: var(--c-white);
    line-height: 20px;
  }
  &__trust-icon {
    font-size: 16px;
    line-height: 1;
  }

  /* 右侧结算器容器 */
  &__calculator {
    flex: 0 0 448px;
  }
}

/* ---------- 模拟结算卡片 ---------- */
.payment-calc {
  background: var(--c-white);
  border-radius: var(--r-xl);
  border: 1px solid var(--c-border-card);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  padding: 25px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 41px;
    border-bottom: 1px solid var(--c-border);
    padding: 0 8px;
    margin-bottom: 16px;
  }
  &__label {
    font-size: 16px;
    font-weight: 700;
    color: var(--c-text-dark);
    line-height: 24px;
  }
  &__badge {
    background: var(--c-border-card);
    border-radius: var(--r-pill);
    padding: 4px 8px;
    font-size: 12px;
    color: var(--c-text-dark);
    line-height: 16px;
  }

  &__field-label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: var(--c-text-dark);
    margin-bottom: 5px;
    line-height: 20px;
  }

  &__input-wrap {
    display: flex;
    align-items: center;
    border: 1px solid var(--c-border);
    border-radius: var(--r-md);
    height: 50px;
    padding: 0 13px;
    margin-bottom: 16px;
  }
  &__input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 16px;
    color: var(--c-text-dark);
    background: transparent;
    line-height: 24px;
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button { appearance: none; margin: 0; }
  }
  &__input-icon {
    font-size: 18px;
    line-height: 1;
    flex-shrink: 0;
  }

  /* 汇率行 */
  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--c-bg-light);
    border-radius: var(--r-md);
    height: 48px;
    padding: 0 12px;
    margin-bottom: 12px;
  }
  &__row-label {
    font-size: 16px;
    color: var(--c-text-dark);
    line-height: 24px;
  }
  &__row-value {
    font-size: 16px;
    font-weight: 700;
    font-family: Roboto, sans-serif;
    color: var(--c-primary-dark);
    line-height: 24px;
  }

  &__disclaimer {
    text-align: center;
    font-size: 12px;
    color: var(--c-gray-light);
    margin: 20px 0 25px;
    line-height: 16px;
  }
}

/* ================================================================
   2. 全球支付网络 标题区
   ================================================================ */
.payment-network {
  &__inner {
    max-width: var(--max-w);
    margin: 0 auto;
    padding: 116px 0 0;
    text-align: center;
  }
  &__label {
    font-size: 20px;
    color: var(--c-primary);
    margin: 0 0 16px;
    line-height: 20px;
  }
  &__title {
    font-size: 32px;
    font-weight: 700;
    color: var(--c-text-heading);
    margin: 0 0 16px;
    line-height: 32px;
  }
  &__desc {
    font-size: 20px;
    color: var(--c-text-muted);
    margin: 0;
    line-height: 20px;
  }
}

/* ================================================================
   3. Tab 标签栏 + 内容区
   ================================================================ */
.payment-tabs {
  &__inner {
    max-width: var(--max-w);
    margin: 60px auto 0;
  }

  /* Tab 头部按钮 */
  &__header {
    display: flex;
    border-bottom: 1px solid #ccc;
  }
  &__btn {
    flex: 1;
    background: none;
    border: none;
    padding: 0 0 12px;
    height: auto;
    font-size: 30px;
    font-family: 'Source Han Sans CN', sans-serif;
    color: var(--c-text-body);
    cursor: pointer;
    text-align: center;
    transition: color 0.2s;
    &--active {
      color: var(--c-primary);
      font-weight: 500;
    }
  }

  /* 活动指示器 */
  &__indicator {
    height: 6px;
    background: transparent;
    position: relative;
    &-bar {
      width: 33.333%;
      height: 6px;
      background: var(--c-primary);
      transition: transform 0.3s ease;
    }
  }

  &__content {
    margin-top: 24px;
  }
}

/* Tab 内容面板 */
.payment-tab-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  min-height: 350px;

  &__text {
    flex: 0 0 600px;
    padding-top: 102px;
  }
  &__title {
    font-size: 24px;
    font-weight: 500;
    color: var(--c-text-heading);
    margin: 0;
    line-height: 36px;
  }
  &__desc {
    font-size: 14px;
    color: var(--c-text-body);
    margin: 20px 0 0;
    line-height: 1.5;
  }
  &__badge {
    background: #f7f7f7;
    border-radius: var(--r-xs);
    display: inline-flex;
    align-items: center;
    padding: 0;
    height: 28px;
    width: 60px;
    margin-top: 20px;
    img { width: 42px; height: 12px; margin: auto; }
  }
  &__logos {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0;
    margin-top: 20px;
  }
  &__logo {
    height: 28px;
    width: auto;
    object-fit: contain;
    margin-right: 0;
    padding: 0 4px;
  }
  &__img {
    width: 500px;
    height: 350px;
    object-fit: contain;
    flex-shrink: 0;
  }
}

/* ================================================================
   4. 为什么选择 Taoify 收款
   ================================================================ */
.payment-why {
  margin-top: 116px;
  min-height: 500px;
  background: url('~@/assets/images/payment/chooseus-bg.png') center no-repeat;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;

  &__inner {
    max-width: var(--max-w);
    width: 100%;
    margin: 0 auto;
    padding: 61px 0;
    text-align: center;
  }
  &__title {
    font-size: 32px;
    font-weight: 700;
    color: var(--c-white);
    margin: 0;
    line-height: 36px;
  }
  &__subtitle {
    font-size: 20px;
    color: var(--c-white);
    margin: 6px 0 40px;
    line-height: 24px;
  }
  &__grid {
    display: grid;
    grid-template-columns: repeat(4, 282px);
    gap: 24px;
    justify-content: center;
  }
  &__card {
    background: rgba(255, 255, 255, 0.15);
    border-radius: var(--r-lg);
    padding: 40px 24px;
    text-align: left;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    &-icon {
      width: 60px;
      height: 60px;
      margin-bottom: 32px;
    }
    &-title {
      font-size: 20px;
      font-weight: 700;
      font-family: Inter, sans-serif;
      color: var(--c-white);
      margin: 0 0 24px;
      line-height: 28px;
    }
    &-desc {
      font-size: 16px;
      font-family: Inter, sans-serif;
      color: var(--c-white);
      margin: 0;
      line-height: 24px;
    }
  }
}

/* ================================================================
   5. 多币种功能 + 汇率计算
   ================================================================ */
.payment-multi {
  &__inner {
    max-width: var(--max-w);
    margin: 175px auto 0;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  /* 左侧：功能介绍 */
  &__features {
    flex: 0 0 551px;
    padding-top: 23px;
  }
  &__features-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }
  &__features-icon {
    font-size: 36px;
    line-height: 1;
    flex-shrink: 0;
  }
  &__features-title {
    font-size: 32px;
    font-weight: 700;
    color: var(--c-text-dark);
    margin: 0;
    line-height: 40px;
  }
  &__features-desc {
    font-size: 16px;
    color: var(--c-text-gray);
    line-height: 24px;
    margin: 0 0 48px;
  }

  /* 轮播 */
  &__carousel {
    position: relative;
    height: 96px;
    overflow: hidden;
    cursor: pointer;
  }
  &__carousel-track {
    transition: transform 0.4s ease;
  }
  &__carousel-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    height: 96px;
    padding: 2px 0;
    opacity: 0.4;
    transition: opacity 0.3s;
    &__carousel-icon {
      font-size: 20px;
      line-height: 1;
      flex-shrink: 0;
      margin-top: 2px;
    }
    &--active { opacity: 1; }
  }
  &__carousel-text {
    p {
      margin: 0;
      font-size: 16px;
      color: var(--c-text-dark);
      line-height: 24px;
      & + p { margin-top: 12px; }
    }
  }

  /* 右侧：汇率计算示例 */
  &__rates {
    flex: 0 0 601px;
    background: var(--c-bg-light);
    border-radius: var(--r-lg);
    border: 1px solid var(--c-border-purple);
    padding: 25px 25px 40px;
  }
  &__rates-title {
    font-size: 20px;
    font-weight: 700;
    color: var(--c-text-dark);
    margin: 0 0 16px;
    line-height: 28px;
  }
  &__rates-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0;
    height: 24px;
    margin-bottom: 8px;
  }
  &__rates-from {
    font-size: 16px;
    color: var(--c-text-extra);
    line-height: 24px;
  }
  &__rates-to {
    font-size: 16px;
    font-weight: 500;
    font-family: Roboto, sans-serif;
    color: var(--c-text-extra);
    line-height: 24px;
  }
  &__rates-tip {
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--c-white);
    border-radius: var(--r-md);
    padding: 16px;
    margin-top: 24px;
    img { width: 20px; height: 20px; flex-shrink: 0; }
    span {
      font-size: 14px;
      font-weight: 500;
      color: var(--c-primary-dark);
      line-height: 20px;
    }
  }
}

/* ================================================================
   6. 3步极速接入
   ================================================================ */
.payment-steps {
  &__inner {
    max-width: var(--max-w);
    margin: 0 auto;
    padding-top: 120px;
    text-align: center;
  }
  &__title {
    font-size: 32px;
    font-weight: 700;
    color: var(--c-text-heading);
    margin: 0;
    line-height: 32px;
  }
  &__subtitle {
    font-size: 20px;
    color: var(--c-text-muted);
    margin: 6px 0 0;
    line-height: 20px;
  }
  &__divider {
    position: relative;
    width: 100%;
    height: 12px;
    background: rgba(115, 49, 255, 0.1);
    margin-top: 75px;
  }
  &__timeline {
    display: flex;
    justify-content: center;
    gap: 148px;
    margin-top: -46px;
    position: relative;
    z-index: 1;
  }
  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
    flex: 0 0 257px;
  }
  &__badge {
    width: 80px;
    height: 80px;
    border-radius: var(--r-pill);
    background: var(--c-primary-gradient-45);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 30px;
    font-weight: 700;
    font-family: Inter, sans-serif;
    color: var(--c-white);
    line-height: 36px;
    margin-bottom: 20px;
  }
  &__content {
    text-align: center;
  }
  &__item-title {
    font-size: 20px;
    font-weight: 500;
    color: var(--c-text-dark);
    margin: 0;
    line-height: 28px;
  }
  &__item-desc {
    font-size: 16px;
    color: var(--c-text-light-gray);
    margin: 8px 0 0;
    line-height: 24px;
  }

  &__action {
    display: flex;
    justify-content: center;
    margin-top: 40px;
  }
  &__link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: var(--c-bg-purple-light);
    border: none;
    border-radius: var(--r-pill);
    padding: 12px 24px;
    cursor: pointer;
    transition: opacity 0.15s;
    font-size: 16px;
    font-weight: 700;
    color: var(--c-primary-dark);
    line-height: 24px;
    &:hover { opacity: 0.8; }
    img { width: 20px; height: 20px; }
  }
}

/* ================================================================
   8. 常见问题
   ================================================================ */
.payment-faq {
  max-width: var(--max-w);
  margin: 0 auto;
  padding: 60px 20px;

  &__title {
    font-size: 30px;
    font-weight: 700;
    color: var(--c-text-heading);
    text-align: center;
    margin: 0 0 32px;
  }

  &__list {
    max-width: 800px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 0;
  }
  &__item {
    border-bottom: 1px solid #eee;
    padding: 16px 0;
    &--open {
      .payment-faq__q::after {
        content: '−';
      }
    }
  }

  &__q {
    font-size: 18px;
    font-weight: 500;
    color: var(--c-text-heading);
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    &::after {
      content: '+';
      font-size: 22px;
      color: var(--c-text-muted);
      flex-shrink: 0;
    }
  }

  &__a {
    font-size: 14px;
    color: var(--c-text-body);
    margin: 8px 0 0;
    line-height: 1.6;
  }
}

/* ================================================================
   8. 银行级安全
   ================================================================ */
.payment-security {
  background: var(--c-bg-light);
  padding-bottom: 1px;

  &__inner {
    max-width: var(--max-w);
    margin: 0 auto;
    padding: 64px 0 0;
    text-align: center;
  }
  &__title {
    font-size: 32px;
    font-weight: 700;
    color: var(--c-text-dark);
    margin: 0 0 40px;
    line-height: 36px;
  }

  /* 6 列网格 */
  &__grid {
    display: grid;
    grid-template-columns: repeat(6, 184px);
    gap: 20px;
    justify-content: center;
  }
  &__card {
    background: var(--c-white);
    border-radius: var(--r-md);
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    padding: 16px 0;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  &__card-icon {
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    img { width: auto; height: auto; max-width: 64px; max-height: 64px; }
  }
  &__card-icon-fallback {
    width: 40px;
    height: 57px;
    background: #abc1ff;
    position: relative;
    border-radius: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    span {
      font-size: 24px;
      color: #f66;
      font-weight: 700;
    }
  }
  &__card-text {
    text-align: center;
  }
  &__card-title {
    font-size: 16px;
    font-weight: 500;
    color: var(--c-text-dark);
    margin: 0;
    line-height: 24px;
  }
  &__card-desc {
    font-size: 12px;
    color: var(--c-text-light-gray);
    margin: 4px 0 0;
    line-height: 16px;
  }

  /* 承诺横幅 */
  &__commit {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: var(--c-white);
    border-radius: var(--r-md);
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    padding: 20px;
    margin-top: 24px;
    margin-bottom: 64px;
  }
  &__commit-left {
    text-align: left;
  }
  &__commit-title {
    font-size: 20px;
    font-weight: 700;
    color: var(--c-text-dark);
    margin: 0;
    line-height: 28px;
  }
  &__commit-text {
    font-size: 16px;
    color: var(--c-text-gray);
    margin: 4px 0 0;
    line-height: 24px;
  }
  &__commit-highlight {
    font-size: 18px;
    font-weight: 700;
    color: var(--c-green);
    line-height: 28px;
    white-space: nowrap;
  }
}

/* ================================================================
   响应式
   ================================================================ */
@media (max-width: 1280px) {
  .payment-hero__inner,
  .payment-network__inner,
  .payment-tabs__inner,
  .payment-why__inner,
  .payment-multi__inner,
  .payment-steps__inner,
  .payment-security__inner {
    padding-left: 20px;
    padding-right: 20px;
  }
}

@media (max-width: 1024px) {
  .payment-hero {
    &__inner { flex-direction: column; align-items: center; padding: 80px 20px 60px; }
    &__content { flex: auto; width: 100%; padding-top: 0; text-align: center; }
    &__title { font-size: 36px; }
    &__desc { max-width: 100%; }
    &__actions { justify-content: center; }
    &__trust { justify-content: center; flex-wrap: wrap; }
    &__calculator { flex: auto; width: 100%; max-width: 448px; margin-top: 40px; }
  }
  .payment-network__inner { padding-top: 60px; }
  .payment-tabs {
    &__btn { font-size: 20px; }
    &__content { margin-top: 16px; }
  }
  .payment-tab-content {
    flex-direction: column;
    &__text { flex: auto; padding-top: 20px; width: 100%; }
    &__img { width: 100%; height: auto; max-height: 300px; margin-top: 20px; }
  }
  .payment-why {
    margin-top: 60px;
    &__grid { grid-template-columns: repeat(2, 1fr); gap: 16px; max-width: 600px; margin: 0 auto; }
    &__card { align-items: center; text-align: center; padding: 30px 20px; }
  }
  .payment-multi {
    &__inner { flex-direction: column; margin-top: 80px; }
    &__features { flex: auto; width: 100%; }
    &__rates { flex: auto; width: 100%; margin-top: 40px; }
  }
  .payment-steps {
    &__inner { padding-top: 80px; }
    &__timeline { flex-direction: column; align-items: center; gap: 40px; }
  }
  .payment-security {
    &__grid { grid-template-columns: repeat(3, 1fr); max-width: 600px; margin: 0 auto; }
    &__commit { flex-direction: column; gap: 12px; text-align: center; }
    &__commit-left { text-align: center; }
  }
}

@media (max-width: 768px) {
  .payment-hero {
    min-height: auto;
    &__inner { padding: 60px 16px 40px; }
    &__title { font-size: 28px; }
    &__btn { width: 140px; font-size: 15px; height: 44px; }
    &__actions { flex-direction: column; align-items: center; gap: 12px; }
  }
  .payment-network__title { font-size: 24px; }
  .payment-network__desc { font-size: 16px; }
  .payment-tabs {
    &__btn { font-size: 16px; }
  }
  .payment-why {
    &__grid { grid-template-columns: 1fr; }
  }
  .payment-multi {
    &__features-title { font-size: 24px; }
  }
  .payment-steps {
    &__title { font-size: 24px; }
    &__subtitle { font-size: 16px; }
    &__inner { padding-top: 60px; }
    &__divider { display: none; }
    &__timeline { flex-direction: column; align-items: center; gap: 0; margin-top: 0; }
    &__item { flex-direction: row; gap: 16px; width: 100%; max-width: 340px; padding: 16px 0; flex: 0;}
    &__badge { width: 56px; height: 56px; font-size: 22px; flex-shrink: 0; margin-bottom: 0; }
    &__content { text-align: left; }
    &__item-title { font-size: 17px; }
    &__item-desc { font-size: 14px; }
    &__action { margin-top: 24px; }
  }
  .payment-faq {
    &__q { font-size: 16px; }
  }
  .payment-security {
    &__inner { padding-top: 40px; }
    &__grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
    &__title { font-size: 24px; }
  }

}
</style>
